/**
 * Patient Login Page
 *
 * Login page for patients using their document number
 * In Colombia, patients access their records with their ID document
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { Calendar, IdCard, UserCircle } from 'lucide-react'

import { patientLogin } from '@/application/auth/auth.service'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Card, CardHeader, CardDescription, CardContent, CardFooter } from '@/presentation/components/ui/Card'
import { LoginLogo } from '@/presentation/components/ui/AppLogo'
import { APP_ROUTES, DOCUMENT_TYPES, DOCUMENT_TYPE_LABELS } from '@/config/constants'
import { env } from '@/config/env'
import { getErrorMessage } from '@/lib/api'

// =============================================================================
// Validation Schema
// =============================================================================

const patientLoginSchema = z.object({
  documentType: z.enum(['CC', 'TI', 'CE', 'PA', 'RC', 'NIT', 'MS', 'AS', 'CD', 'SC', 'PE', 'NV'], {
    message: 'Seleccione tipo de documento',
  }),
  documentNumber: z
    .string()
    .min(4, 'El número de documento debe tener al menos 4 caracteres')
    .max(20, 'Número de documento muy largo')
    .regex(/^[a-zA-Z0-9]+$/, 'Solo letras y números'),
  birthDate: z
    .string()
    .min(1, 'Indique su fecha de nacimiento')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Indique su fecha de nacimiento'),
})

type PatientLoginFormData = z.infer<typeof patientLoginSchema>

// =============================================================================
// Component
// =============================================================================

export function PatientLoginPage() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PatientLoginFormData>({
    resolver: zodResolver(patientLoginSchema),
    defaultValues: {
      documentType: 'CC',
    },
  })

  const onSubmit = async (data: PatientLoginFormData) => {
    setError(null)
    setIsLoading(true)

    try {
      await patientLogin({
        documentType: data.documentType,
        documentNumber: data.documentNumber,
        birthDate: data.birthDate,
      })
      // Redirect to patient portal
      navigate(APP_ROUTES.PATIENT_PORTAL.HOME)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    /* Mismo arreglo que en el login del personal: con `items-center` un
       contenido mas alto que la pantalla se corta arriba y abajo, y la parte
       de arriba no se puede alcanzar. Aqui importa aun mas: el paciente entra
       casi siempre desde el telefono. */
    <div className="min-h-screen alto-de-pantalla flex flex-col overflow-y-auto bg-gradient-to-br from-teal-900 via-teal-800 to-cyan-900 p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.1)_1px,_transparent_1px)] bg-[length:24px_24px] opacity-30" />

      {/* Main Card */}
      <Card className="w-full max-w-md relative z-10 my-auto mx-auto shadow-2xl animate-fade-in">
        <CardHeader className="space-y-4 text-center">
          {/* Logo */}
          <div className="flex justify-center">
            <LoginLogo subtitle="Portal del Paciente" />
          </div>
          <CardDescription>
            Acceda a sus estudios e informes médicos
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {/* Error Alert */}
            {error && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Document Type Select */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Tipo de documento
              </label>
              <div className="relative">
                <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  {...register('documentType')}
                  className="w-full h-10 pl-10 pr-4 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  disabled={isLoading}
                >
                  {Object.entries(DOCUMENT_TYPES).map(([key, value]) => (
                    <option key={key} value={value}>
                      {DOCUMENT_TYPE_LABELS[value as keyof typeof DOCUMENT_TYPE_LABELS]}
                    </option>
                  ))}
                </select>
              </div>
              {errors.documentType && (
                <p className="text-xs text-red-500">{errors.documentType.message}</p>
              )}
            </div>

            {/* Document Number Input */}
            <Input
              {...register('documentNumber')}
              type="text"
              label="Número de su documento"
              placeholder="Ej: 1234567890"
              error={errors.documentNumber?.message}
              leftIcon={<UserCircle className="h-4 w-4" />}
              autoComplete="username"
              disabled={isLoading}
            />

            {/* Fecha de nacimiento: el segundo dato de acceso.
                Sustituye a la contrasena, que era el propio documento. Se
                eligio por ser lo que el paciente se sabe de memoria: no
                necesita celular, ni senal, ni esperar ningun mensaje. */}
            <div className="space-y-1.5">
              <Input
                {...register('birthDate')}
                type="date"
                label="¿Cuándo nació?"
                error={errors.birthDate?.message}
                leftIcon={<Calendar className="h-4 w-4" />}
                disabled={isLoading}
              />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Su fecha de nacimiento, como aparece en su documento.
              </p>
            </div>
          </CardContent>

          <CardFooter>
            <Button
              type="submit"
              className="w-full bg-teal-600 hover:bg-teal-700"
              size="lg"
              isLoading={isLoading}
            >
              Ingresar a Mi Portal
            </Button>
          </CardFooter>
        </form>
      </Card>

      {/* Footer */}
      <div className="relative z-10 shrink-0 pt-6 text-center text-teal-200 text-sm">
        <p>&copy; {new Date().getFullYear()} {env.APP_NAME}. Todos los derechos reservados.</p>
        <p className="text-xs mt-1 opacity-70">
          Ingrese con su documento de identidad colombiano
        </p>
      </div>
    </div>
  )
}
